import { getTimelineDateRange } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { Goal as AppGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { GoalGroupBy } from 'shared/types/sorting-mode';
import { TimelineTimeframes } from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { createOtherLifeArea } from 'shared/utils/create-other-life-area';

import { applyGoalReorder } from './apply-goal-reorder';
import { buildGoalTimelineCategories } from './build-goal-timeline-categories';
import { GoalTimeline } from './goal-timeline';
import { ReorderPositions } from './types';

export type GoalTimeLineContainerProps = {
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  goals: AppGoal[];
  // Unfiltered set: a sub-goal keeps its inherited life area when a filter drops its parent.
  allGoals: AppGoal[];
  lifeAreas: LifeArea[];
  weekStartsOn: WeekDays;
  title: string;
  groupBy?: GoalGroupBy;
  customOrder?: ID[];
  onChangeGoal?: (
    id: ID,
    updatedGoal: {
      startDate?: Timestamp;
      deadline?: Timestamp;
      goalSorting?: ID[];
      lifeAreaId?: ID | null;
      parentIds?: ID[] | null;
    },
  ) => void;
  onChangeLifeArea?: (
    lifeAreaId: ID,
    updatedLifeArea: { goalSorting: ID[] },
  ) => void;
  onChangeCollapsedItems?: (ids: ID[]) => void;
  collapsedItems?: ID[];
  onCategoryAdd?: (id: ID) => void;
};

export const GoalTimelineContainer: React.FC<GoalTimeLineContainerProps> = ({
  timeframe,
  referenceDate,
  goals,
  allGoals,
  lifeAreas,
  weekStartsOn,
  title,
  groupBy,
  customOrder,
  onChangeGoal,
  onChangeLifeArea,
  onChangeCollapsedItems,
  collapsedItems = [],
  onCategoryAdd,
}) => {
  const { t } = useTranslation();
  const taskCounts = useTaskCounts();
  const otherLifeArea = useMemo(() => createOtherLifeArea(t), [t]);

  const categories = useMemo(
    () =>
      buildGoalTimelineCategories({
        goals,
        allGoals,
        lifeAreas,
        groupBy,
        customOrder,
        otherLifeArea,
        taskCounts: taskCounts?.byGoalId,
        noDeadlineName: t(
          'context-menus.filtering.task.filters.date.presets.no-deadline',
        ),
      }),
    [
      goals,
      allGoals,
      lifeAreas,
      groupBy,
      customOrder,
      otherLifeArea,
      taskCounts?.byGoalId,
      t,
    ],
  );

  const timelineRef = useRef<HTMLDivElement>(null);

  const dateRange = useMemo(
    () => getTimelineDateRange({ referenceDate, timeframe, weekStartsOn }),
    [referenceDate, timeframe, weekStartsOn],
  );

  const _toggleCollapse = (id: ID) =>
    onChangeCollapsedItems?.(
      collapsedItems?.includes(id)
        ? collapsedItems?.filter((itemId) => itemId !== id)
        : [...collapsedItems, id],
    );

  const handleUpdateDates = (
    goalId: string,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => {
    // Call the onChangeGoal callback if provided
    if (onChangeGoal) {
      onChangeGoal(goalId as ID, {
        startDate: newStartDate,
        deadline: newEndDate,
      });
    }
  };

  const handleReorderGoals = (
    draggedId: string,
    targetId: string,
    position: ReorderPositions,
  ) => {
    const result = applyGoalReorder(categories, {
      draggedId,
      targetId,
      position,
    });
    if (!result) {
      return;
    }

    result.changes.goals.forEach(({ id, update }) =>
      onChangeGoal?.(id, update),
    );
    result.changes.lifeAreas.forEach(({ id, update }) =>
      onChangeLifeArea?.(id, update),
    );

    return result.categories;
  };

  return (
    <GoalTimeline
      ref={timelineRef}
      categories={categories}
      goals={goals}
      lifeAreas={lifeAreas}
      dateRange={dateRange}
      onUpdateDates={handleUpdateDates}
      onReorderGoals={handleReorderGoals}
      collapsedItems={collapsedItems}
      toggleCollapse={_toggleCollapse}
      onCategoryAdd={onCategoryAdd}
      timeframe={timeframe}
      referenceDate={referenceDate}
      weekStartsOn={weekStartsOn}
      title={title}
    />
  );
};
